// Articles content — to be enriched by Peggy & Didier
export const articles = [
    {
        slug: "impact-ecrans-cerveau-enfant",
        category: "Neurosciences",
        accent: "brand",
        title: "L'impact des écrans sur le cerveau de l'enfant",
        excerpt:
            "Découvrez comment les écrans affectent la plasticité cérébrale et comment limiter leur impact négatif.",
        readTime: "6 min",
        author: "Peggy",
        date: "Mars 2026",
        img: "https://images.unsplash.com/photo-1491841550275-ad7854e35ca6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
        content: [
            {
                type: "p",
                text: "Le cerveau d'un enfant n'est pas un cerveau d'adulte miniature. Pendant les vingt premières années de la vie, il se sculpte, élague, renforce certaines connexions et en supprime d'autres. C'est ce qu'on appelle la **plasticité cérébrale** — et c'est précisément cette plasticité qui rend nos enfants si vulnérables à l'exposition prolongée aux écrans.",
            },
            {
                type: "h2",
                text: "Ce que disent les neurosciences",
            },
            {
                type: "p",
                text: "Les études récentes (notamment celles de Michel Desmurget, INSERM) montrent qu'au-delà de 2 heures d'écran par jour, on observe chez les enfants de 6 à 12 ans une diminution mesurable de l'épaisseur du cortex préfrontal — la zone du cerveau responsable de la concentration, de la planification et de la maîtrise de soi.",
            },
            {
                type: "p",
                text: "Pire : les contenus à stimulation rapide (réseaux sociaux, jeux mobiles, vidéos courtes) entraînent une libération massive de dopamine. Le cerveau s'habitue à ce niveau de stimulation et trouve fade tout le reste : un livre, un devoir, une discussion en famille...",
            },
            {
                type: "h2",
                text: "Le protocole 3-6-9-12 de Serge Tisseron",
            },
            {
                type: "list",
                items: [
                    "**Avant 3 ans** : pas d'écran du tout (privilégier interactions humaines, livres, jeux libres).",
                    "**De 3 à 6 ans** : pas d'écran personnel, télé uniquement avec un adulte, contenus très limités.",
                    "**De 6 à 9 ans** : pas d'internet seul, premiers contenus accompagnés et discutés.",
                    "**De 9 à 12 ans** : internet accompagné, pas de réseaux sociaux.",
                    "**Après 12 ans** : autonomie progressive avec règles claires sur les usages.",
                ],
            },
            {
                type: "h2",
                text: "Ce que vous pouvez faire dès demain",
            },
            {
                type: "p",
                text: "Notre méthode propose un **protocole de désintoxication progressive sur 21 jours** : une réduction graduée, des activités de remplacement, et l'installation de rituels « hors-écran » en famille. Les résultats sont visibles dès la deuxième semaine : meilleur sommeil, concentration retrouvée, communication apaisée.",
            },
            {
                type: "p",
                text: "Ce n'est pas une diabolisation de la technologie — c'est une **utilisation alignée avec ce que le cerveau peut digérer** à chaque âge.",
            },
        ],
    },
    {
        slug: "methode-stop-emotions",
        category: "Stoïcisme",
        accent: "pinky",
        title: "La méthode STOP pour gérer les émotions",
        excerpt:
            "Une technique stoïcienne simple pour aider votre enfant à prendre du recul avant de réagir.",
        readTime: "8 min",
        author: "Didier",
        date: "Mars 2026",
        img: "https://images.pexels.com/photos/3457273/pexels-photo-3457273.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
        content: [
            {
                type: "p",
                text: "« Ce ne sont pas les choses qui troublent les humains, mais l'opinion qu'ils en ont. » Épictète, *Manuel*. Il y a 2 000 ans, un philosophe stoïcien proposait déjà l'idée révolutionnaire qui sous-tend aujourd'hui les thérapies cognitives modernes : entre l'événement et notre réaction, il y a **un espace** — et dans cet espace réside notre liberté.",
            },
            {
                type: "h2",
                text: "Qu'est-ce que la méthode STOP ?",
            },
            {
                type: "p",
                text: "STOP est un acronyme — un protocole en 4 temps que votre enfant peut apprendre dès 7 ans. C'est une **micro-pause stoïcienne** à utiliser quand l'émotion monte (frustration, colère, panique, jalousie...).",
            },
            {
                type: "list",
                items: [
                    "**S — Stop.** On s'arrête. Concrètement : on lève la main, on dit (ou pense) « STOP ». On ne fait plus rien pendant 3 secondes.",
                    "**T — Take a breath.** On respire profondément. 4 secondes inspiration, 4 secondes expiration. On relâche les épaules.",
                    "**O — Observe.** On observe ce qui se passe : « Mon cœur bat vite, mes mains sont serrées, j'ai chaud. Je suis en colère. »",
                    "**P — Proceed / Plan.** On choisit consciemment la prochaine action : « Qu'est-ce qui dépend de moi ? Qu'est-ce qui m'aiderait vraiment ? »",
                ],
            },
            {
                type: "h2",
                text: "Pourquoi ça marche (la science)",
            },
            {
                type: "p",
                text: "Quand l'amygdale (cerveau émotionnel) prend le dessus, le cortex préfrontal (cerveau rationnel) se déconnecte temporairement. STOP donne au cortex préfrontal le temps de se rallumer. C'est exactement ce que les stoïciens appelaient **prosochê** — l'attention vigilante portée à soi-même.",
            },
            {
                type: "h2",
                text: "Comment l'enseigner à votre enfant",
            },
            {
                type: "p",
                text: "1. **À froid**, expliquez la méthode comme un super-pouvoir. 2. **Affichez-la** dans la chambre ou le couloir avec les 4 lettres. 3. **Pratiquez** la respiration ensemble 2 minutes le soir. 4. **Quand une crise arrive**, ne l'enseignez pas en plein orage — accompagnez l'émotion, et le lendemain, racontez ensemble ce qui s'est passé.",
            },
            {
                type: "p",
                text: "En 3 semaines, la plupart des enfants commencent à utiliser STOP **seuls**. Et avec le temps, c'est tout un rapport à soi-même qui change : ils ne sont plus emportés par leurs émotions — ils les **traversent**.",
            },
        ],
    },
    {
        slug: "systeme-familial-haute-performance",
        category: "Organisation",
        accent: "ink",
        title: "Le Système Familial Haute Performance™",
        excerpt:
            "Organisation, routines et environnement optimal pour éliminer le chaos et créer un quotidien fluide.",
        readTime: "7 min",
        author: "Peggy & Didier",
        date: "Mars 2026",
        img: "https://images.pexels.com/photos/4971022/pexels-photo-4971022.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
        content: [
            {
                type: "p",
                text: "La plupart des familles qui nous consultent ne souffrent pas d'un manque d'amour ou de bonne volonté. Elles souffrent d'un **manque de système**. Chaque matin redevient une bataille parce que rien n'est anticipé. Chaque soir explose parce que rien n'est cadré. Le Système Familial Haute Performance™ change cela.",
            },
            {
                type: "h2",
                text: "Le principe : éliminer la friction",
            },
            {
                type: "p",
                text: "Inspiré du Lean management et appliqué à la sphère familiale, le système repose sur une idée simple : **dans une journée, 70 % de la friction est prévisible et évitable**. Cartables non préparés, vêtements pas choisis, papiers d'école oubliés... Ce ne sont pas des fatalités, ce sont des **erreurs de système**.",
            },
            {
                type: "h2",
                text: "Les 4 piliers du SFHP™",
            },
            {
                type: "list",
                items: [
                    "**Routine du matin cadrée** : 6 étapes maximum, affichées visuellement, dans le même ordre chaque jour. L'enfant n'a plus à *penser* — il *exécute*.",
                    "**Rituel du soir apaisant** : déconnexion progressive 1h avant le coucher, préparation du lendemain, 5 minutes de qualité parent-enfant.",
                    "**Environnement optimal** : une place pour chaque chose, du matériel d'école pré-positionné, des zones « calme » et zones « jeu » bien distinctes.",
                    "**Règles d'autonomie claires** : 3 règles maximum par âge, affichées, discutées, jamais négociées dans la crise.",
                ],
            },
            {
                type: "h2",
                text: "Résultats observés en 30 jours",
            },
            {
                type: "p",
                text: "Sur les 500+ familles accompagnées, voici les constats récurrents après un mois d'application : baisse de **75 %** des conflits matinaux, gain de **45 minutes** en moyenne par soirée, et — peut-être le plus important — une **sérénité retrouvée** pour les parents.",
            },
            {
                type: "p",
                text: "Le système ne remplace pas l'amour ni l'éducation. Il les **libère** : quand le quotidien tourne, on retrouve l'espace mental pour l'essentiel.",
            },
        ],
    },
];

export function getArticle(slug) {
    return articles.find((a) => a.slug === slug);
}
